import { RolePermission } from '../models/Roles';

/**
 * Obtiene el permiso .view correspondiente a un slug
 * Busca desde el nivel más específico hasta el más general
 * Ejemplos:
 * - 'orders.create' -> 'orders.view'
 * - 'cod-payments.files.upload' -> 'cod-payments.files.view'
 * - 'cod-payments.payments.add' -> 'cod-payments.payments.view'
 * @param slug El slug del permiso
 * @param allPermissions Lista de todos los permisos disponibles
 * @returns El slug del permiso .view o null si no existe
 */
export const getViewPermission = (
  slug: string,
  allPermissions: RolePermission[]
): string | null => {
  if (!slug || slug.endsWith('.view')) return null;

  // Buscar desde el nivel más específico hasta el más general
  // Ejemplo: 'cod-payments.files.upload' -> buscar 'cod-payments.files.view', luego 'cod-payments.view'
  const parts = slug.split('.');
  
  // Empezar desde el nivel más específico (todos los segmentos excepto el último)
  for (let i = parts.length - 1; i > 0; i--) {
    const baseGroup = parts.slice(0, i).join('.');
    const viewSlug = `${baseGroup}.view`;
    
    // Verificar si existe el permiso .view en la lista de permisos disponibles
    const viewPermission = allPermissions.find((p) => p.slug === viewSlug);
    if (viewPermission) {
      return viewSlug;
    }
  }

  return null;
};

/**
 * Verifica si hay otros permisos del mismo grupo seleccionados (excluyendo .view)
 * @param baseGroup El prefijo del grupo (ej: 'orders', 'bank-accounts')
 * @param selectedPerms Array de permisos seleccionados
 * @param allPermissions Lista de todos los permisos disponibles
 * @returns true si hay otros permisos del grupo seleccionados
 */
export const hasOtherPermissionsFromGroup = (
  baseGroup: string,
  selectedPerms: string[],
  allPermissions: RolePermission[]
): boolean => {
  return allPermissions.some(
    (p) =>
      p.slug.startsWith(baseGroup + '.') &&
      !p.slug.endsWith('.view') &&
      selectedPerms.includes(p.slug)
  );
};

/**
 * Obtiene el prefijo base de un permiso (parte antes del primer punto)
 * Ejemplo: 'orders.create' -> 'orders'
 * @param slug El slug del permiso
 * @returns El prefijo base
 */
export const getPermissionPrefix = (slug: string): string => {
  const firstDotIndex = slug.indexOf('.');
  return firstDotIndex !== -1 ? slug.substring(0, firstDotIndex) : slug;
};

/**
 * Obtiene el grupo base de un permiso .view (parte antes del último punto)
 * Ejemplo: 'orders.view' -> 'orders'
 * @param slug El slug del permiso .view
 * @returns El grupo base
 */
export const getViewPermissionBaseGroup = (slug: string): string => {
  const lastDotIndex = slug.lastIndexOf('.');
  return lastDotIndex !== -1 ? slug.substring(0, lastDotIndex) : slug;
};

